/**
 * The pseudo-locale (MDC-4668): English with every letter swapped for an accented look-alike, about
 * 30% longer, and bracketed — `⟦Ŝàvé~~⟧` for "Save".
 *
 * It is not a language anyone reads. It is how a developer sees, on any screen of a dev build, what
 * still isn't translated: a string that comes out plain was never passed through `t()`. The brackets
 * show where a label is clipped, and the padding stands in for the longer words most languages use
 * (Italian runs about a quarter longer than English), so a layout that only fits English shows it.
 *
 * Off by default, and only ever on in a dev build:
 * - dashboard: `REACT_APP_PSEUDO_LOCALE=true pnpm --filter dashboard dev`
 * - mobile: `EXPO_PUBLIC_PSEUDO_LOCALE=true` in the environment of `expo start`
 */

const ACCENTED: Readonly<Record<string, string>> = {
  a: 'à',
  b: 'ƀ',
  c: 'ç',
  d: 'ð',
  e: 'é',
  f: 'ƒ',
  g: 'ĝ',
  h: 'ĥ',
  i: 'î',
  j: 'ĵ',
  k: 'ķ',
  l: 'ļ',
  m: 'ɱ',
  n: 'ñ',
  o: 'ö',
  p: 'þ',
  q: 'ǫ',
  r: 'ŕ',
  s: 'š',
  t: 'ţ',
  u: 'û',
  v: 'ṽ',
  w: 'ŵ',
  x: 'ẋ',
  y: 'ý',
  z: 'ž',
  A: 'À',
  B: 'Ɓ',
  C: 'Ç',
  D: 'Ð',
  E: 'É',
  F: 'Ƒ',
  G: 'Ĝ',
  H: 'Ĥ',
  I: 'Î',
  J: 'Ĵ',
  K: 'Ķ',
  L: 'Ļ',
  M: 'Ṁ',
  N: 'Ñ',
  O: 'Ö',
  P: 'Þ',
  Q: 'Ǫ',
  R: 'Ŕ',
  S: 'Š',
  T: 'Ţ',
  U: 'Û',
  V: 'Ṽ',
  W: 'Ŵ',
  X: 'Ẋ',
  Y: 'Ý',
  Z: 'Ž',
};

/** How much longer than the English the pseudo text runs. */
const EXPANSION = 0.3;

/**
 * A `<Trans>` component tag — `<email>`, `</report>`, `<0/>` — which must reach React untouched, or
 * the element it names is lost. Everything outside the tags is accented.
 */
const TAG = /(<\/?[\w.-]+\s*\/?>)/;

/** `text` as the pseudo-locale shows it. An empty string stays empty. */
export const pseudoLocalize = (text: string): string => {
  if (text.length === 0) {
    return text;
  }

  const parts = text.split(TAG);
  const accented = parts
    .map((part) => (TAG.test(part) ? part : [...part].map((char) => ACCENTED[char] ?? char).join('')))
    .join('');
  // Padded for the text a reader sees, not for the tags around it.
  const visibleLength = parts.filter((part) => !TAG.test(part)).join('').length;

  return `⟦${accented}${'~'.repeat(Math.ceil(visibleLength * EXPANSION))}⟧`;
};

/** The name the post-processor is registered under on the shared instance. */
export const PSEUDO_POST_PROCESSOR = 'pseudo';

/** The i18next post-processor that applies `pseudoLocalize` to every translated string. */
export const pseudoPostProcessor = {
  type: 'postProcessor',
  name: PSEUDO_POST_PROCESSOR,
  process: (value: string) => pseudoLocalize(value),
} as const;
